import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import { copyTextToClipboard } from "../../ui-kit/kuika-copy-button/copy-text-to-clipboard";
import { KUIKA_COPY_RESET_MS } from "../../ui-kit/kuika-copy-button/kuika-copy-button";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import { BoardService } from "./board-service";
import ConfirmDialog from "./confirm-dialog";
import { confirmDialog } from "./confirm-service";
import { currentUserId } from "./current-user";
import IssueDetail, { NewEpicChildren } from "./issue-detail";
import { BoardStatus, Issue, Project, UpdateIssueInput } from "./types";

export interface IssuePanelProps {
  issue: Issue;
  statuses: BoardStatus[];
  project: Project | undefined;
  // Canonical permalink to this issue, copied by the "Copy link" action. Unused in new-issue mode.
  shareUrl?: string;
  onClose: () => void;
  onExpand?: () => void;
  onDelete?: (issueId: string) => void;
  // Optimistically patch the held issue so rail edits show instantly (see IssueDetail.onPatchIssue).
  // In new-issue mode this updates the parent's draft.
  onPatchIssue?: (patch: UpdateIssueInput) => void;
  // The board root, so outside-click dismissal can tell a board click from a portaled popup
  // (date pickers / selectboxes / menus render at <body>, outside this element).
  boardRootRef: React.MutableRefObject<HTMLDivElement | null>;
  // New-issue mode: the drawer hosts a draft (IssueDetail isNewIssue). The header shows a Create
  // action instead of the key / status / watch / delete chrome; onCreate persists the draft.
  isNewIssue?: boolean;
  onCreate?: () => void;
  // New epic draft only: queue child issues before the epic is created.
  newEpicChildren?: NewEpicChildren;
  // Whether `issue` has been hydrated to the full record (incl. description) yet — forwarded to
  // IssueDetail so the description shows a skeleton until the by-key fetch lands (the drawer seeds
  // `issue` from a light card first). Defaults to true.
  descriptionReady?: boolean;
}

// Click-target test for issue-opening surfaces: a pointerdown on one of these SWITCHES the open
// issue rather than dismissing the drawer. This is intentionally a selector, not refs — it matches
// a *category* of elements spread across four sibling views (cards, list rows, backlog rows,
// roadmap rows/bars). A ref registry would force every one of those components to register/
// unregister each element's DOM node with the panel — more code, more coupling, more fragile than
// one class list owned here. Add any new issue-opening surface's class here.
const ISSUE_OPEN_SELECTOR: string =
  ".kuika-board__card, .kuika-board-list__row, .kuika-board-backlog__row, .kuika-board-roadmap__rail-row, .kuika-board-roadmap__epic-bar";

const IssuePanel: React.FC<IssuePanelProps> = ({
  issue,
  statuses,
  project,
  shareUrl,
  onClose,
  onExpand,
  onDelete,
  onPatchIssue,
  boardRootRef,
  isNewIssue = false,
  onCreate,
  newEpicChildren,
  descriptionReady = true
}) => {
  const canCreate: boolean = Boolean(issue.summary.trim()) && Boolean(issue.projectKey) && Boolean(issue.statusId);
  // Brief "Copied!" confirmation on the copy-link button (resets after a moment).
  const [linkCopied, setLinkCopied] = useState<boolean>(false);
  const copyTimerRef = useRef<number | null>(null);
  useEffect(() => {
    return () => {
      if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
    };
  }, []);
  const copyLink = useCallback((): void => {
    if (!shareUrl) return;
    // The shared helper falls back to the textarea copy when the Clipboard API is missing or refused,
    // so the check only goes up for a copy that really happened.
    void copyTextToClipboard(shareUrl).then((succeeded: boolean): void => {
      if (!succeeded) return;
      setLinkCopied(true);
      if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = window.setTimeout(() => setLinkCopied(false), KUIKA_COPY_RESET_MS);
    });
  }, [shareUrl]);
  const panelRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  // Play the slide-out animation before the parent unmounts us, so open and
  // close are symmetric. requestClose flips `closing`; once the exit animation
  // ends we call the real onClose (which navigates and unmounts).
  const [closing, setClosing] = useState<boolean>(false);
  const requestClose = useCallback((): void => setClosing(true), []);

  // A new-issue draft is "dirty" once the user has put anything into it. Closing a dirty draft
  // (Cancel / Esc / outside-click) asks for confirmation so a stray click doesn't discard work. A
  // ref mirrors it so the long-lived key/click handlers read the latest without re-registering.
  const isDirty: boolean =
    isNewIssue &&
    (Boolean(issue.summary.trim()) ||
      Boolean(issue.descriptionMarkdown.trim()) ||
      issue.labels.length > 0 ||
      issue.assigneeUserId != null ||
      issue.storyPoints != null ||
      Boolean(issue.dueDate) ||
      Boolean(issue.startDate) ||
      Boolean(issue.parentIssueId) ||
      Boolean(issue.fixVersionId));
  const dirtyRef = useRef<boolean>(isDirty);
  dirtyRef.current = isDirty;
  // Closing a dirty draft opens a confirm modal instead of discarding immediately. The ref lets the
  // window key/click handlers ignore dismissals while the confirm modal is up.
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState<boolean>(false);
  const confirmOpenRef = useRef<boolean>(false);
  confirmOpenRef.current = confirmDiscardOpen;
  const attemptClose = useCallback((): void => {
    if (dirtyRef.current) {
      setConfirmDiscardOpen(true);
      return;
    }
    requestClose();
  }, [requestClose]);

  // A new issue id means we're switching issues, not closing — reset the flag.
  useEffect(() => {
    setClosing(false);
  }, [issue.id]);

  useEffect(() => {
    if (!closing) return;
    const timer: number = window.setTimeout(onClose, 200);
    return () => window.clearTimeout(timer);
  }, [closing, onClose]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (confirmOpenRef.current) return; // the confirm modal owns Esc while it's open
      if (event.key === "Escape") {
        event.stopPropagation();
        attemptClose();
      }
    };
    // The overlay is pointer-events:none (the board stays interactive behind the
    // drawer), so a backdrop onClick never fires. Close when a pointer-down lands on
    // the board surface but outside the panel. Crucially, we require the target to be
    // INSIDE the board root (.kuika-board): portaled popups/dropdowns (date picker,
    // every KuikaSelectbox/rc-select, dropdown menus, …) render at <body>, outside the
    // board root, so they're excluded automatically — no per-control allow-list to
    // maintain, and any future portaled control is handled for free.
    const onPointerDown = (event: MouseEvent): void => {
      if (confirmOpenRef.current) return; // ignore board clicks while confirming a discard
      const target: Node | null = event.target as Node | null;
      if (!(target instanceof Element)) return;
      if (panelRef.current?.contains(target)) return; // inside the drawer
      if (!boardRootRef.current?.contains(target)) return; // a portaled popup, not the board behind
      // Clicking an issue surface (card / list row / backlog row / roadmap epic) SWITCHES the open
      // issue rather than dismissing — the drawer stays mounted and just swaps content, so don't
      // animate it closed (doing so flickers: slide-out starts, then the switch cancels it). In
      // new-issue mode there's no switching, so any outside click dismisses.
      if (!isNewIssue && target.closest(ISSUE_OPEN_SELECTOR)) return;
      attemptClose();
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [attemptClose, isNewIssue, boardRootRef]);

  return (
    <>
      <div className="kuika-board__panel-overlay" role="dialog" aria-modal="true">
        <div
          ref={panelRef}
          className={`kuika-board__panel${closing ? " kuika-board__panel--closing" : ""}`}
          data-testid="board-issue-panel"
        >
          <div className="kuika-board__panel-header">
            {isNewIssue ? (
              <>
                <span className="kuika-board__panel-title">New issue</span>
                <div className="kuika-board__panel-actions">
                  <KuikaButton type="tertiary" color="neutral" size="sm" onClick={attemptClose}>
                    Cancel
                  </KuikaButton>
                  <KuikaButton type="primary" size="sm" onClick={onCreate} disabled={!canCreate}>
                    Create issue
                  </KuikaButton>
                </div>
              </>
            ) : (
              <>
                <span className="kuika-board__panel-key">{issue.key}</span>
                <div className="kuika-board__panel-actions">
                  <KuikaTooltip content={linkCopied ? "Copied!" : "Copy link"} position="bottom">
                    <KuikaButton
                      type="tertiary"
                      color="neutral"
                      size="sm"
                      isIcon
                      onClick={copyLink}
                      data-testid="board-issue-copy-link"
                      aria-label="Copy link to issue"
                      className={linkCopied ? "kuika-board__copy-active" : ""}
                    >
                      <KuikaSvg name={linkCopied ? "check" : "link"} width={16} height={16} />
                    </KuikaButton>
                  </KuikaTooltip>
                  <KuikaTooltip
                    content={issue.watcherUserIds.includes(currentUserId()) ? "Unwatch" : "Watch"}
                    position="bottom"
                  >
                    <KuikaButton
                      type="tertiary"
                      color="neutral"
                      size="sm"
                      isIcon
                      onClick={() => BoardService.toggleWatch(issue.id)}
                      data-testid="board-issue-watch"
                      className={issue.watcherUserIds.includes(currentUserId()) ? "kuika-board__watch-active" : ""}
                    >
                      <KuikaSvg
                        name={issue.watcherUserIds.includes(currentUserId()) ? "eye-closed" : "eye"}
                        width={16}
                        height={16}
                      />
                    </KuikaButton>
                  </KuikaTooltip>
                  <KuikaTooltip content="Open in full page" position="bottom">
                    <KuikaButton
                      type="tertiary"
                      color="neutral"
                      size="sm"
                      isIcon
                      onClick={onExpand}
                      data-testid="board-issue-expand"
                    >
                      <KuikaSvg name="maximize" width={16} height={16} />
                    </KuikaButton>
                  </KuikaTooltip>
                  <KuikaTooltip content="Delete issue" position="bottom">
                    <KuikaButton
                      type="tertiary"
                      color="danger"
                      size="sm"
                      isIcon
                      onClick={() => {
                        void confirmDialog({
                          title: "Delete issue",
                          message: `Delete ${issue.key}? This can't be undone.`,
                          confirmLabel: "Delete",
                          danger: true
                        }).then((ok: boolean) => {
                          if (ok) onDelete?.(issue.id);
                        });
                      }}
                      data-testid="board-issue-delete"
                    >
                      <KuikaSvg name="trash" width={16} height={16} />
                    </KuikaButton>
                  </KuikaTooltip>
                  <KuikaTooltip content="Close (esc)" position="bottom">
                    <KuikaButton
                      type="tertiary"
                      color="neutral"
                      size="sm"
                      isIcon
                      onClick={requestClose}
                      data-testid="board-issue-close"
                    >
                      <KuikaSvg name="x" width={16} height={16} />
                    </KuikaButton>
                  </KuikaTooltip>
                </div>
              </>
            )}
          </div>
          <IssueDetail
            issue={issue}
            statuses={statuses}
            project={project}
            layout="drawer"
            onPatchIssue={onPatchIssue}
            isNewIssue={isNewIssue}
            descriptionReady={descriptionReady}
            newEpicChildren={newEpicChildren}
          />
        </div>
      </div>
      <ConfirmDialog
        open={confirmDiscardOpen}
        title="Discard new issue?"
        message="This issue hasn't been created yet. Your changes won't be saved."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        danger
        onConfirm={() => {
          setConfirmDiscardOpen(false);
          requestClose();
        }}
        onCancel={() => setConfirmDiscardOpen(false)}
      />
    </>
  );
};

export default IssuePanel;
